/* eslint-disable react/display-name, react/no-unstable-nested-components */
import {AutocompleteInputChangeReason, AutocompleteRenderInputParams, ClickAwayListener, css, PopperProps, useTheme} from '@mui/material';
import {
  DlMuiAutocomplete,
  DlMuiAutocompleteOwnProps,
  DlMuiPopper,
  DLSelect,
  DlSelectProps,
  GetStylesFunction,
  hierarchySyncContainer,
  hierarchyTreeModel,
  StringUtils,
} from '@wallaby-ui/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ValidateError} from 'src/js/common';
import {FilterNode as FilterNodeModel} from 'src/js/model/filters_config';

import styles from './FilterNodeTypeHierarchyMultiSelect.less';
import {HIERARCHY_MULTI_SELECT_WIDTH_PX} from '../filtering_constants';
import {getStateFromUserActionEnum, UserActionEnum} from '../filtering_helpers';

type HierarchyPopperProps = {
  hierarchyData: hierarchyTreeModel.HierarchyNodeFlat[];
  node: FilterNodeModel;
  handlePopperAndHierarchyNodeClick: (node: hierarchyTreeModel.HierarchyTreeNode) => void;
  hierarchyIsOpened: any;
  highlightedValues?: string[];
  getOptionDisabled?: (row: any) => boolean;
  muiStyles: any;
};

export type FilterNodeTypeHierarchySyncMultiSelectOwnProps = DlSelectProps &
  DlMuiAutocompleteOwnProps & {
    isMui?: boolean;
    hierarchyData: hierarchyTreeModel.HierarchyNodeFlat[];
    hierarchyNodeClick: any;
    isOpened?: boolean;
    isMulti?: boolean;
    highlightedValues?: string[];
    node: FilterNodeModel;
    error?: ValidateError;
    getOptionDisabled?: (row: any) => boolean;
    renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
    marginBlock?: string;
    maxWidth?: string | number;
  };

type Props = FilterNodeTypeHierarchySyncMultiSelectOwnProps;

const FilterNodeTypeHierarchySyncMultiSelectComponent: React.FC<Props> = (props) => {
  const {
    isMulti,
    components,
    tabIndex,
    hierarchyData,
    hierarchyNodeClick,
    isOpened,
    node,
    isMui,
    highlightedValues,
    error,
    getOptionDisabled,
    marginBlock,
    maxWidth,
    ...restProps
  } = props;

  const theme = useTheme();
  const muiStyles = getStyles({theme, params: {marginBlock, maxWidth}});

  const dlSelectComponent = isMulti ? <DLSelect isMulti={isMulti} components={components} tabIndex={tabIndex} {...restProps} /> : null;

  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState<boolean>(false);
  const [isPopperOpen, setIsPopperOpen] = useState<boolean>(false);
  const isMounted = useRef(false);

  const handleOnInputChange = (_event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    if (!isMounted.current) return; // NOTE: skip first change for single input
    if (!isMulti && reason === 'reset') return; // NOTE: skip change if caused by controlled value
    if (value.length === 0) {
      const {returnIsPopperOpen, returnIsAutocompleteOpen} = getStateFromUserActionEnum(
        isAutocompleteOpen,
        isPopperOpen,
        isMulti,
        UserActionEnum.EmptyInput
      );
      setIsPopperOpen(returnIsPopperOpen);
      setIsAutocompleteOpen(returnIsAutocompleteOpen);
    } else {
      const {returnIsPopperOpen, returnIsAutocompleteOpen} = getStateFromUserActionEnum(
        isAutocompleteOpen,
        isPopperOpen,
        isMulti,
        UserActionEnum.AnyInput
      );
      setIsPopperOpen(returnIsPopperOpen);
      setIsAutocompleteOpen(returnIsAutocompleteOpen);
    }
  };

  const handleOnOpen = (event) => {
    isMounted.current = true;
    if (event.type === 'click' || event.type === 'mousedown') {
      const {returnIsPopperOpen, returnIsAutocompleteOpen} = getStateFromUserActionEnum(
        isAutocompleteOpen,
        isPopperOpen,
        isMulti,
        UserActionEnum.Open
      );
      setIsPopperOpen(returnIsPopperOpen);
      setIsAutocompleteOpen(returnIsAutocompleteOpen);
    }
  };

  const handleOnClose = () => {
    // NOTE: this also gets called on select, so then we set isAutocompleteFocused back to true everywhere
    const {returnIsPopperOpen, returnIsAutocompleteOpen} = getStateFromUserActionEnum(
      isAutocompleteOpen,
      isPopperOpen,
      isMulti,
      UserActionEnum.Close
    );
    setIsPopperOpen(returnIsPopperOpen);
    setIsAutocompleteOpen(returnIsAutocompleteOpen);
  };

  const handlePopperAndHierarchyNodeClick = useCallback(
    (node: hierarchyTreeModel.HierarchyTreeNode) => {
      hierarchyNodeClick(node);
      const {returnIsPopperOpen, returnIsAutocompleteOpen} = getStateFromUserActionEnum(
        isAutocompleteOpen,
        isPopperOpen,
        isMulti,
        UserActionEnum.ClickHierarchy
      );
      setIsPopperOpen(returnIsPopperOpen);
      setIsAutocompleteOpen(returnIsAutocompleteOpen);
    },
    [hierarchyNodeClick, isAutocompleteOpen, isPopperOpen, isMulti]
  );

  const handleClickAway = () => {
    const {returnIsPopperOpen, returnIsAutocompleteOpen} = getStateFromUserActionEnum(
      isAutocompleteOpen,
      isPopperOpen,
      isMulti,
      UserActionEnum.ClickAway
    );
    setIsPopperOpen(returnIsPopperOpen);
    setIsAutocompleteOpen(returnIsAutocompleteOpen);
  };

  const popperStateRef = useRef({
    node,
    hierarchyData,
    handlePopperAndHierarchyNodeClick,
    hierarchyIsOpened: isOpened,
    getOptionDisabled,
    highlightedValues,
    muiStyles,
  } as HierarchyPopperProps);
  useEffect(() => {
    popperStateRef.current = {
      node,
      hierarchyData,
      handlePopperAndHierarchyNodeClick,
      hierarchyIsOpened: isOpened,
      getOptionDisabled,
      highlightedValues,
      muiStyles,
    };
  }, [node, hierarchyData, handlePopperAndHierarchyNodeClick, isOpened, getOptionDisabled, highlightedValues, muiStyles]);
  const HierarchyPopper = React.useCallback(
    (popperProps: PopperProps) => {
      const popperState = popperStateRef.current;
      const {muiStyles} = popperState;
      return (
        <DlMuiPopper
          {...popperProps}
          open={isPopperOpen}
          style={{
            width: `${HIERARCHY_MULTI_SELECT_WIDTH_PX}px`, // NOTE: `width: 100%` doesn't work here because it's a stupid absolute position element..
            maxWidth: `${HIERARCHY_MULTI_SELECT_WIDTH_PX}px`,
          }}
          inputCss={muiStyles.popper}
          hasTooltip
        >
          <ul css={muiStyles.dlHierarchyTree}>
            <hierarchySyncContainer.HierarchySyncContainer
              data={popperState.hierarchyData}
            hierarchyTypeId={popperState.node.getName()} // eslint-disable-line
              onNodeClick={popperState.handlePopperAndHierarchyNodeClick}
              isTogglingSeparated
              showRoot={false}
              tabIndex={popperState.hierarchyIsOpened ? 0 : -1}
              highlightedValues={highlightedValues}
              getOptionDisabled={popperState.getOptionDisabled}
            />
          </ul>
        </DlMuiPopper>
      );
    },
    [isPopperOpen, highlightedValues] // NOTE: we should not be using PopperComponent={} in the first place, but here we are...
    // TODO: don't blink even once? (remove highlightedValues, change HierarchyNodeItem styles)
  );
  return (
    <div data-name={'filter-node-type-hierarchy-sync-multi-select--wrapper'}>
      {isMui ? (
        <ClickAwayListener onClickAway={handleClickAway}>
          <div data-name={'filter-node-type-hierarchy-sync-multi-select--autocompleteWrapper'} css={muiStyles.filterWrapper}>
            <DlMuiAutocomplete
              disablePortal
              componentsProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: false,
                    },
                  ],
                },
              }}
              limitTags={isPopperOpen ? undefined : 1}
              open={isAutocompleteOpen}
              onInputChange={handleOnInputChange}
              onClose={handleOnClose}
              onOpen={handleOnOpen}
              isMulti={isMulti}
              inputCss={muiStyles.select}
              PopperComponent={isPopperOpen ? HierarchyPopper : undefined}
              {...restProps}
              maxWidth={maxWidth}
              error={error?.bool}
              helperText={error?.string}
              getOptionDisabled={getOptionDisabled}
            />
          </div>
        </ClickAwayListener>
      ) : (
        dlSelectComponent
      )}
      <div className={'dlFilterNodeContentInput'}>
        {isMui ? null : (
          <ul className={styles.dlHierarchyTree}>
            <hierarchySyncContainer.HierarchySyncContainer
              data={hierarchyData}
              hierarchyTypeId={node.getName()}
              onNodeClick={hierarchyNodeClick}
              isTogglingSeparated
              showRoot={false}
              tabIndex={isOpened ? 0 : -1}
              highlightedValues={highlightedValues}
              getOptionDisabled={getOptionDisabled}
            />
          </ul>
        )}
      </div>
    </div>
  );
};

FilterNodeTypeHierarchySyncMultiSelectComponent.displayName = 'FilterNodeTypeHierarchySyncMultiSelect';

export const FilterNodeTypeHierarchySyncMultiSelect = React.memo(FilterNodeTypeHierarchySyncMultiSelectComponent);

const getStyles: GetStylesFunction = (options) => {
  const maxWidth = options?.params?.maxWidth;
  return {
    // NOTE: `width: 100%` is broken in some places, so we use fixed width by default
    filterWrapper: css`
      display: flex;
      margin: ${options?.params?.marginBlock || '8px'} 0;
      width: ${StringUtils.addPx(maxWidth === undefined ? HIERARCHY_MULTI_SELECT_WIDTH_PX : '100%')};
      max-width: ${StringUtils.addPx(maxWidth === undefined ? HIERARCHY_MULTI_SELECT_WIDTH_PX : maxWidth)};
    `,
    select: css`
      position: relative;
      width: 100%;
    `,
    popper: css`
      background-color: ${options?.theme?.palette.common.white};
      box-shadow: 0 0 12px 0 #12161914;
      max-height: 450px;
      overflow: auto;
      border-bottom: 10px solid ${options?.theme?.palette.common.white};
    `,
    popperContent: css``,
    dlHierarchyTree: css`
      width: 100%;

      ul& {
        border: 0;
        margin: 0;
        padding: 0;
      }
    `,
  };
};
